import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import { useForm, useFetch, useTheme, useStore, useToast, useModal, useResource, useGrid, usePage } from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_ROLE_FILE_FORM,
    SF_OGA_USR_INFO,
    SG_OGA_USR_ROLE_LIST,
} from "./services/OsciUsrRoleService";
import { Link } from "react-router-dom";
import { OSCI0104027Q } from "./OSCI0104027Q";

/**
 * Internal role Details
 * !==  내부 사용자 역할 상세 ==!
 *
 */
export const OSCI0104026S = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0002"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const param = useParams();
    const { theme } = useTheme();
    const fileUuid = "";

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        ogaUsrInfo: useForm({ defaultSchema: SF_OGA_USR_INFO }),
        form_roleFileForm: useForm({ defaultSchema: SF_ROLE_FILE_FORM }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        ogaUsrRoleList: useGrid({ defaultSchema: SG_OGA_USR_ROLE_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getOgaUsrRoleList: useFetch({
            api: (data) => APIS.getOgaUsrInfo(data),
            onSuccess: (data: any) => {
                const { content } = data.usrInfo;
                form.ogaUsrInfo.setValues(content);
            },
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        saveOgaUsrRoleList: useFetch({
            api: () =>
                APIS.saveOgaUsrRoleList({
                    usrIdntfId: param["usrIdntfId"],
                    ogaUsrRoleList: grid.ogaUsrRoleList.getData(),
                }),
            onSuccess: () => {
                modal.openModal({ content: "msg.osc.00073" }); // 정상적으로 저장되었습니다.
                fetch.getOgaUsrRoleList.fetch({ usrIdntfId: param["usrIdntfId"] });
            },
            onError: () => modal.openModal({ content: "msg.osc.00045" }), // Failed to save. !==  저장이 실패하였습니다. ==!
        }),
    };

    /*
     * Render functions for grid cells
     * !== 그리드 셀 렌더링 함수 ==!
     */
    const render = {
        cell: {
            attchFle: (props: any) => {
                const rowData = props.rowValues;
                const isNewRow = rowData?.__type === "added";

                console.log("rowData", rowData);

                // get bsopId from fileSaveReqDtoList
                const bsopId = rowData.fileSaveDto?.fileSaveReqDtoList?.[0]?.bsopId || rowData.roleId;

                // set the form value
                form.form_roleFileForm.setValues({ fileGrpUuid: bsopId });

                const fileGrpUuid = form.form_roleFileForm.getValues("fileGrpUuid");
                console.log("fileGrpUuid", fileGrpUuid);

                if (isNewRow) {
                    // For new rows, show file upload control
                    return (
                        <div className="w-full">
                            <Group.Control
                                {...form.form_roleFileForm.schema.fileGrpUuid}
                                onChange={(fileSaveDto: any) => {
                                    const updatedRow = {
                                        ...rowData,
                                        roleMemoFileUuid: fileSaveDto.fileGrpUuid,
                                        fileSaveDto,
                                    };

                                    const rowIndex = grid.ogaUsrRoleList.getData().findIndex((r: any) => r === rowData);
                                    grid.ogaUsrRoleList.updateRow(updatedRow);
                                }}
                            />
                        </div>
                    );
                } else {
                    // For existing rows, show file input or placeholder
                    return rowData.roleMemoFileUuid ? (
                        <div className="w-full">
                            <Group.Control type="file" value={bsopId} edit={false} />
                        </div>
                    ) : (
                        <p>No file</p>
                    );
                }
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onBtnSaveClick: () => {
            const list = grid.ogaUsrRoleList.getData({ excludes: ["origin"] });
            console.log(list);
            if (list.length === 0) {
                modal.openModal({ content: "msg.osc.00218" }); // There is no data to be stored and deleted. !== 저장 및 삭제 대상 데이터가 없습니다. ==!
                return;
            }

            list.forEach((item: any) => {
                if (item.fileGrpUuid && typeof item.fileGrpUuid === "object") {
                    item.fileSaveDto = item.fileGrpUuid; // Attach the FileSaveDto
                    item.roleMemoFileUuid = null; // Clear the old UUID field
                    item.fileGrpUuid = null; // Clear temporary field
                }
            });

            modal.openModal({
                content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => fetch.saveOgaUsrRoleList.fetch(),
            });
        },
        onBtnGrdAddRowClick: () => {
            const data = grid.ogaUsrRoleList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.osc.00219" });
                return;
            }

            const modalCallback = (list: any) => {
                const roleList = grid.ogaUsrRoleList.getData();
                list.forEach((item: any) => {
                    const exists = roleList.findIndex((subItem: any) => item.roleId === subItem.roleId) !== -1;
                    if (!exists) {
                        grid.ogaUsrRoleList.addRow({
                            ...item,
                            __type: "added",
                            fileSaveDto: null,
                            roleMemoFileUuid: null,
                        });
                    }
                });
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "md",
                content: (
                    <OSCI0104027Q
                        usrIdntfId={param["usrIdntfId"]}
                        ogaUsrRoleList={grid.ogaUsrRoleList.getData()}
                        callback={modalCallback}
                    />
                ),
            });
        },
        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.ogaUsrRoleList.getChecked())) {
                modal.openModal({ content: "msg.osc.00084" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.osc.00062",
                onConfirm: () => {
                    grid.ogaUsrRoleList.deleteRow("checkbox");
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOgaUsrRoleList.fetch({ usrIdntfId: param["usrIdntfId"] });
    }, [theme]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.psnTin} />
                            <Group.Control {...form.ogaUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.orgCd} />
                            <Group.Control {...form.ogaUsrInfo.schema.deptCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.orgRppnYn} />
                            {/* <Group.Control {...form.ogaUsrInfo.schema.frgnrYn} /> */}
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.aplcCntyCd} />
                            <Group.Control {...form.ogaUsrInfo.schema.usrMoblNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.usrEmlAddr} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.rsdtAddr} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            {/* <Group.Control {...form.ogaUsrInfo.schema.cstmOfceCd} /> */}
                            <Group.Control {...form.ogaUsrInfo.schema.usrStatCd} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid
                            {...grid.ogaUsrRoleList.grid}
                            data={fetch.getOgaUsrRoleList.data?.ogaUsrRoleList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Button role={"list"} onClick={() => navigate(`${URLS.osci0104025q}`)} />
                </Layout.Left>
                <Layout.Right>
                    <Button role={"save"} onClick={handler.onBtnSaveClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
